<template>
  <div
    ref="itemRef"
    v-tooltip.permanent="tooltip"
    class="app-modal-tray-item"
    :class="itemClasses"
    @click="handleClick"
  >
    <div ref="titleRef" class="app-modal-tray-item__title">{{ displayName }}</div>
    <XMarkIcon class="app-modal-tray-item__close" @click="handleClose" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue'
import { useElementVisibility, useResizeObserver } from '@vueuse/core'

import { AssetClass, ContactClass, DocumentClass, TagClass, TransactionClass } from '@types'

import { ASSET_FIELD, ASSET_ID_PREFIX, DOCUMENT_ID_PREFIX, TAG_FIELD, DOCUMENT_FIELD, TAG_ID_PREFIX, UNKNOWN_VALUE, TRANSACTION_ID_PREFIX, TRANSACTION_FIELD, CONTACT_ID_PREFIX } from '@/const'

import { useModalsStore } from '@/store/modals'

import { ModalFactoryInstance } from '../ModalFactory/utils/types'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { stringToLocalDateString } from '@/helpers/dates'
import { CONTACT_FIELD } from '@/entities/contacts/utils/const'
import { Entity } from '@/entities/utils/common'

type Props = {
  modal: ModalFactoryInstance
  modalKey: string
  isFrontModal?: boolean
}

type Emits = {
  click: []
}

const emit = defineEmits<Emits>()

const props = defineProps<Props>()

const modalsStore = useModalsStore()

const itemRef = useTemplateRef('itemRef')
const titleRef = useTemplateRef('titleRef')
const isVisible = useElementVisibility(itemRef, { threshold: 1 })

useResizeObserver(titleRef, () => {
  if (!titleRef.value) return
  isLabelEllipsis.value = titleRef.value.offsetWidth < titleRef.value.scrollWidth
})


const isLabelEllipsis = ref(false)

const itemClasses = computed(() => ({
  'app-modal-tray-item--front': props.isFrontModal,
  'app-modal-tray-item--hidden': !isVisible.value,
}))

const displayName = computed(() => {
  if (!(props.modal.entity instanceof Entity)) return props.modalKey || UNKNOWN_VALUE
  if (props.modalKey.startsWith(ASSET_ID_PREFIX)) {
    return (props.modal.entity as AssetClass).field<string>(ASSET_FIELD.NAME).value || UNKNOWN_VALUE
  } else if (props.modalKey.startsWith(TAG_ID_PREFIX)) {
    const tag = (props.modal.entity as TagClass)
    if (!tag) return UNKNOWN_VALUE
    return `${tag.field<string>(TAG_FIELD.TAG_NAME).value}: ${tag.field<string>(TAG_FIELD.TAG_VALUE).value}`
  } else if (props.modalKey.startsWith(DOCUMENT_ID_PREFIX)) {
    return (props.modal.entity as DocumentClass).field<string>(DOCUMENT_FIELD.NAME).value || UNKNOWN_VALUE
  } else if (props.modalKey.startsWith(TRANSACTION_ID_PREFIX)) {
    const transaction = (props.modal.entity as TransactionClass)
    if (!transaction) return UNKNOWN_VALUE
    const date = stringToLocalDateString(transaction.field<string>(TRANSACTION_FIELD.DATE).value)
    return `${date}: ${transaction.field<string>(TRANSACTION_FIELD.TYPE).value}`
  } else if (props.modalKey.startsWith(CONTACT_ID_PREFIX)) {
    const contact = (props.modal.entity as ContactClass)
    if (!contact) return UNKNOWN_VALUE
    return `${contact.field<string>(CONTACT_FIELD.NAME).value}: ${contact.field<string>(CONTACT_FIELD.EMAIL).value || contact.field<string>(CONTACT_FIELD.PHONE).value}`
  } else {
    return UNKNOWN_VALUE
  }
})

const tooltip = computed(() => {
  if (!isLabelEllipsis.value) return

  return {
    content: displayName.value,
    handleResize: true,
  }
})

const handleClick = () => {
  if (props.isFrontModal) {
    props.modal.hide()
  } else {
    props.modal.show(modalsStore.getZIndex())
  }
  emit('click')
}

const handleClose = (event: Event) => {
  emit('click')
  event.stopPropagation()
  props.modal.conditionalClose(true)
}
</script>

<style>
.app-modal-tray-item {
  @apply flex items-center gap-2;
  @apply p-1;
  @apply border border-common-border;
  @apply bg-body hover:bg-body-gray;
  @apply rounded-md;
  @apply cursor-pointer;

  &--hidden {
    @apply opacity-0;
    @apply pointer-events-none;
  }

  &--front {
    @apply bg-primary-light border-primary;
  }

  &__title {
    @apply max-w-44;
    @apply pl-3;
    @apply text-xs text-common-text;
    @apply truncate;
  }

  &__close {
    @apply w-4 h-4;
    @apply text-light-text;
  }
}
</style>
